// plan-18 S04: commune stage widget — bible §45

import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { commune } from './generate'
import type { CommuneResult } from './generate'
import { qHash } from './seed'
import { normalize } from './normalize'
import { load, save as saveSave, set } from '../../core/save'
import { play } from '../../audio/sfx'
import { streamText } from '../../oracle/streaming'
import { STREAM_CPS } from '../../oracle/cadence'
import { exportCard, downloadBlob } from '../../core/exportCard'
import { renderCommuneContent } from './exportContent'

const MAX_LOG = 48
const MAX_VISIBLE_HISTORY = 12

export const communeWidget: StageWidget = {
  id: 'commune',
  label: 'COMMUNE // ASK ONCE',

  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    let dismissed = false
    const s = load()

    // layout: split-pane on wide, stacked on narrow
    const wrapper = document.createElement('div')
    wrapper.className = 'vt-commune-wrapper'

    const leftCol = document.createElement('div')
    leftCol.className = 'vt-commune-left'

    // input form
    const form = document.createElement('form')
    form.className = 'vt-commune-form'
    const input = document.createElement('input')
    input.type = 'text'
    input.className = 'vt-commune-input'
    input.setAttribute('aria-label', 'ask the oracle')
    input.setAttribute('autocomplete', 'off')
    input.setAttribute('spellcheck', 'false')
    input.placeholder = 'type your question...'
    form.appendChild(input)
    const btn = document.createElement('button')
    btn.type = 'submit'
    btn.className = 'vt-commune-btn'
    btn.textContent = '[ commune ]'
    form.appendChild(btn)
    leftCol.appendChild(form)

    // search bar
    const searchWrap = document.createElement('div')
    searchWrap.className = 'vt-commune-search-wrap'
    const searchInput = document.createElement('input')
    searchInput.type = 'text'
    searchInput.className = 'vt-commune-search'
    searchInput.setAttribute('aria-label', 'search past questions')
    searchInput.placeholder = '// search history'
    searchWrap.appendChild(searchInput)
    leftCol.appendChild(searchWrap)

    // recent questions list
    const historyList = document.createElement('div')
    historyList.className = 'vt-commune-history'
    historyList.setAttribute('aria-label', 'recent questions')
    leftCol.appendChild(historyList)

    wrapper.appendChild(leftCol)

    // right column: current answer
    const rightCol = document.createElement('div')
    rightCol.className = 'vt-commune-right'
    rightCol.setAttribute('aria-live', 'polite')

    const answerArea = document.createElement('div')
    answerArea.className = 'vt-commune-answer'
    rightCol.appendChild(answerArea)

    // share area
    const shareArea = document.createElement('div')
    shareArea.className = 'vt-commune-share'
    shareArea.style.display = 'none'
    rightCol.appendChild(shareArea)

    wrapper.appendChild(rightCol)
    host.appendChild(wrapper)

    // check if we have a pre-loaded question (from URL or command)
    const pendingQ = ctx.args[0] || ''

    // render history
    function renderHistory(filter = ''): void {
      historyList.innerHTML = ''
      const log = s.communeLog || []
      const filtered = filter
        ? log.filter(e => e.q.toLowerCase().includes(filter.toLowerCase()))
        : log
      const visible = filtered.slice(-MAX_VISIBLE_HISTORY)
      for (const entry of visible) {
        const row = document.createElement('div')
        row.className = 'vt-commune-history-row'
        row.textContent = entry.q
        row.title = `@${entry.qHash}`
        row.addEventListener('click', () => {
          if (dismissed) return
          submitQuestion(entry.q)
        })
        historyList.appendChild(row)
      }
    }

    renderHistory()

    // search filter
    searchInput.addEventListener('input', () => {
      renderHistory(searchInput.value)
    })

    // plan-19 S07: track current result for export
    let currentResult: CommuneResult | null = null

    // submit handler
    async function submitQuestion(question: string): Promise<void> {
      if (dismissed) return
      const normalized = normalize(question)
      if (!normalized) {
        answerArea.innerHTML = ''
        for (const line of ['> commune: question unresolved.', '> the oracle received nothing to interpret.', '> probably.']) {
          const div = document.createElement('div')
          div.className = 'vt-commune-line'
          div.textContent = line
          answerArea.appendChild(div)
        }
        return
      }

      const hash = qHash(question)
      const result = commune(question)
      currentResult = result

      answerArea.innerHTML = ''
      shareArea.innerHTML = ''
      shareArea.style.display = 'none'

      // question header
      const qHeader = document.createElement('div')
      qHeader.className = 'vt-commune-q-header'
      qHeader.textContent = `> question: ${result.question}`
      answerArea.appendChild(qHeader)

      const divider = document.createElement('div')
      divider.className = 'vt-commune-divider'
      divider.textContent = '-- commune --'
      answerArea.appendChild(divider)

      // stream the 3 lines at 28 cps
      play('command_accept')
      const reduced = document.body.classList.contains('vt-reduced-motion')

      for (const line of result.lines) {
        const div = document.createElement('div')
        div.className = 'vt-commune-line'
        answerArea.appendChild(div)
        if (reduced) {
          div.textContent = line
        } else {
          await streamText(div, line, { cps: STREAM_CPS })
        }
      }

      // end divider
      const endDiv = document.createElement('div')
      endDiv.className = 'vt-commune-divider'
      endDiv.textContent = '-- //'
      answerArea.appendChild(endDiv)

      // commune_resolve SFX on last line
      play('commune_resolve')

      // repeat detection
      const log = s.communeLog || []
      const isRepeat = log.some(e => e.qHash === hash)

      if (isRepeat) {
        const sub = document.createElement('div')
        sub.className = 'vt-commune-subscript'
        sub.textContent = '// you have asked this before. answer holds.'
        answerArea.appendChild(sub)
      }

      // save to log
      if (!isRepeat) {
        if (!s.communeLog) s.communeLog = []
        s.communeLog.push({ qHash: hash, q: result.question, answerSeed: hash, at: Date.now() })
        if (s.communeLog.length > MAX_LOG) {
          s.communeLog = s.communeLog.slice(-MAX_LOG)
        }
        saveSave(s)
      }

      // render history
      renderHistory(searchInput.value)

      // share area
      shareArea.style.display = ''
      const hashLine = document.createElement('div')
      hashLine.className = 'vt-commune-share-line'
      hashLine.textContent = `@qHash: ${hash}`
      shareArea.appendChild(hashLine)

      // hash share URL
      const hashUrl = `vaguepostking.com/#commune=${hash}`
      const hashCopy = document.createElement('button')
      hashCopy.className = 'vt-commune-copy-btn'
      hashCopy.textContent = `[ copy ]`
      hashCopy.addEventListener('click', () => {
        navigator.clipboard.writeText(`https://${hashUrl}`).catch(() => {})
        play('command_accept')
        hashCopy.style.color = 'var(--vt-amber)'
        setTimeout(() => { hashCopy.style.color = '' }, 1600)
      })
      const hashShare = document.createElement('div')
      hashShare.className = 'vt-commune-share-line'
      hashShare.textContent = `@share: ${hashUrl} `
      hashShare.appendChild(hashCopy)
      shareArea.appendChild(hashShare)

      // text share URL
      const b32 = btoa(result.question).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_')
      const textUrl = `vaguepostking.com/#commune=q:${b32}`
      const textCopy = document.createElement('button')
      textCopy.className = 'vt-commune-copy-btn'
      textCopy.textContent = `[ copy ]`
      textCopy.addEventListener('click', () => {
        navigator.clipboard.writeText(`https://${textUrl}`).catch(() => {})
        play('command_accept')
        textCopy.style.color = 'var(--vt-amber)'
        setTimeout(() => { textCopy.style.color = '' }, 1600)
      })
      const textShare = document.createElement('div')
      textShare.className = 'vt-commune-share-line'
      textShare.textContent = `@share: ${textUrl} `
      textShare.appendChild(textCopy)
      shareArea.appendChild(textShare)

      // plan-19 S07: [ export ] button — full-width row beneath the share URLs
      const exportRow = document.createElement('div')
      exportRow.className = 'vt-commune-share-line'
      const exportBtn = document.createElement('button')
      exportBtn.className = 'vt-commune-copy-btn'
      exportBtn.textContent = '[ export ]'
      exportBtn.addEventListener('click', async () => {
        if (!currentResult) return
        play('command_accept')
        const originalLabel = exportBtn.textContent
        exportBtn.textContent = '[ rendering... ]'
        exportBtn.style.pointerEvents = 'none'
        try {
          const qh = currentResult.qHash
          const filename = `vpk-commune-${qh.slice(0, 6)}.png`
          const blob = await exportCard({
            title: 'COMMUNE // ASK ONCE',
            headerLine: `> ${currentResult.question || 'commune'}`,
            content: { kind: 'text', render: renderCommuneContent(currentResult) },
            stampUrl: `vaguepostking.com/#commune=${qh}`,
            stampHash: `@${qh.slice(0, 4)}`,
            filename,
          })
          downloadBlob(blob, filename)
          play('quorum_chime')
          const sv = load()
          set('cardExports', { ...sv.cardExports, commune: sv.cardExports.commune + 1 })
          rightCol.style.outline = '1px solid var(--vt-amber)'
          setTimeout(() => { rightCol.style.outline = '' }, 1600)
        } catch {
          play('command_reject')
          const errDiv = document.createElement('div')
          errDiv.className = 'vt-commune-subscript'
          errDiv.textContent = '// export failed. consensus: probably.'
          answerArea.appendChild(errDiv)
        } finally {
          exportBtn.textContent = originalLabel
          exportBtn.style.pointerEvents = ''
        }
      })
      exportRow.appendChild(exportBtn)
      shareArea.appendChild(exportRow)
    }

    // form submit
    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const val = input.value.trim()
      if (!val) return
      input.value = ''
      submitQuestion(val)
    })

    // if we have a pending question from args, submit it
    if (pendingQ) {
      input.value = pendingQ
      submitQuestion(pendingQ)
    }

    // if we have a pending hash (from URL), show the Q&A or placeholder
    const pendingHash = ctx.args.find(a => a.startsWith('hash:'))
    if (pendingHash && !pendingQ) {
      const h = pendingHash.slice(5)
      const log = s.communeLog || []
      const entry = log.find(e => e.qHash === h)
      if (entry) {
        submitQuestion(entry.q)
      } else {
        answerArea.innerHTML = ''
        const ph = document.createElement('div')
        ph.className = 'vt-commune-placeholder'
        ph.textContent = `question: [hashed: ${h}]`
        answerArea.appendChild(ph)
        const hint = document.createElement('div')
        hint.className = 'vt-commune-hint'
        hint.textContent = 'type the question to reveal the answer.'
        answerArea.appendChild(hint)

        // listen for input that matches
        form.addEventListener('submit', function hashHandler(e) {
          e.preventDefault()
          const val = input.value.trim()
          if (val && qHash(val) === h) {
            form.removeEventListener('submit', hashHandler)
            input.value = ''
            submitQuestion(val)
          } else {
            input.value = ''
            const retry = document.createElement('div')
            retry.className = 'vt-commune-subscript'
            retry.textContent = '// hash mismatch. try again.'
            answerArea.appendChild(retry)
          }
        })
      }
    }

    return {
      dismiss() {
        dismissed = true
      },
      onSwap(args?: string[]) {
        const question = args?.[0] || ''
        if (question && !question.startsWith('hash:')) {
          input.value = question
          submitQuestion(question)
        } else if (question.startsWith('hash:')) {
          const h = question.slice(5)
          const log = s.communeLog || []
          const entry = log.find(e => e.qHash === h)
          if (entry) {
            submitQuestion(entry.q)
          } else {
            answerArea.innerHTML = ''
            const ph = document.createElement('div')
            ph.className = 'vt-commune-placeholder'
            ph.textContent = `question: [hashed: ${h}]`
            answerArea.appendChild(ph)
            const hint = document.createElement('div')
            hint.className = 'vt-commune-hint'
            hint.textContent = 'type the question to reveal the answer.'
            answerArea.appendChild(hint)
          }
        } else {
          input.focus()
        }
      },
    }
  },
}
